/**
 * Settings — split into purpose-driven sub-pages:
 *   Profile    — interaction style, themes, notes (how Meeri talks to you)
 *   Brokerage  — Schwab connection, SIM/LIVE mode, kill switch
 *   Guardrails — Approval policy, risk caps, playbook, autonomy dial
 *   System     — LLM config (operational, rarely touched)
 *
 * v0.5.4 AE-068: tab selection is now URL state (`?tab=guardrails&sub=playbook`)
 * so FocalCard deeplinks land on the right sub-page, refresh preserves
 * intent, and the back button works.
 */
import { useSearchParams } from 'react-router-dom';
import { ProfileSettings } from './settings/ProfileSettings';
import { BrokerageSettings } from './settings/BrokerageSettings';
import { GuardrailsSettings } from './settings/GuardrailsSettings';
import { SystemSettings } from './settings/SystemSettings';
import { PageHeader, PAGE_MAX_W_NARROW } from './PageHeader';

const TABS = ['profile', 'brokerage', 'guardrails', 'system'] as const;
type SettingsTab = typeof TABS[number];

const LABELS: Record<SettingsTab, string> = {
  profile: 'Profile',
  brokerage: 'Brokerage',
  guardrails: 'Guardrails',
  system: 'System',
};

export function SettingsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = (searchParams.get('tab') ?? 'profile').toLowerCase();
  const tab: SettingsTab = (TABS as readonly string[]).includes(rawTab)
    ? (rawTab as SettingsTab)
    : 'profile';

  function setTab(next: SettingsTab) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'profile') {
      sp.delete('tab');
    } else {
      sp.set('tab', next);
    }
    // Switching tabs clears the sub-tab so it doesn't bleed into a
    // tab that doesn't have one.
    sp.delete('sub');
    setSearchParams(sp, { replace: true });
  }

  return (
    <div className={`${PAGE_MAX_W_NARROW} mx-auto`}>
      <PageHeader
        title="Settings"
        subtitle="Profile, brokerage, guardrails, and system configuration."
      />
      <div className="flex gap-1 mb-4 border-b border-edge pb-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`settings-tab-${t}`}
            className={`text-xs px-3 py-1.5 rounded-md transition ${
              tab === t
                ? 'bg-bg-elev border border-edge text-ink font-medium'
                : 'text-ink-mute hover:text-ink'
            }`}
          >
            {LABELS[t]}
          </button>
        ))}
      </div>
      {tab === 'profile' && <ProfileSettings />}
      {tab === 'brokerage' && <BrokerageSettings />}
      {tab === 'guardrails' && <GuardrailsSettings />}
      {tab === 'system' && <SystemSettings />}
    </div>
  );
}
